import { styled } from '@mui/material';
import { useEffect, useState } from 'react';
import config from 'src/config';

const LOGO_STORAGE_KEY = 'app_logo_media';

const resolveLogoSrc = (logo: any | null): string | null => {
  if (!logo) return null;
  if (typeof logo === 'string') return logo;
  if (typeof logo.image === 'string') return logo.image;
  if (typeof logo.url === 'string') return logo.url;
  if (typeof logo.fileUrl === 'string') return logo.fileUrl;
  if (typeof logo.file === 'string') return logo.file;
  return null;
};

const getStoredLogo = (): any | null => {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(LOGO_STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    window.localStorage.removeItem(LOGO_STORAGE_KEY);
    return null;
  }
};

const LogoWrapper = styled('div')(
  ({ theme }) => `
        color: ${theme.palette.text.primary};
        display: flex;
        align-items:center;
        justify-content:center;
        text-decoration: none;
        width: 100px;
        margin: 0 auto;
        font-weight: ${theme.typography.fontWeightBold};
`
);

function Logo() {
  const [logoSrc, setLogoSrc] = useState<string | null>(() => resolveLogoSrc(getStoredLogo()));

  useEffect(() => {
    const handleLogoUpdate = (event: Event) => {
      const detail = (event as CustomEvent).detail ?? null;
      setLogoSrc(resolveLogoSrc(detail));
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== LOGO_STORAGE_KEY) return;
      let value: any = null;
      if (event.newValue) {
        try {
          value = JSON.parse(event.newValue);
        } catch {
          value = null;
        }
      }
      setLogoSrc(resolveLogoSrc(value));
    };
    window.addEventListener('app-logo-updated', handleLogoUpdate);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('app-logo-updated', handleLogoUpdate);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchLogo = async () => {
      try {
        const res = await fetch(`${config.apiUrl}/api/public/media/logo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        });
        if (!res.ok) throw new Error(`Failed to load logo (${res.status})`);
        const data = await res.json();
        if (!isMounted) return;
        const logoMedia = data?.content ?? data;
        const resolved = resolveLogoSrc(logoMedia ?? null);
        if (resolved) {
          setLogoSrc(resolved);
        }
        if (typeof window !== 'undefined') {
          if (resolved && logoMedia) {
            window.localStorage.setItem(LOGO_STORAGE_KEY, JSON.stringify(logoMedia));
          } else {
            window.localStorage.removeItem(LOGO_STORAGE_KEY);
          }
          if (typeof window.dispatchEvent === 'function') {
            window.dispatchEvent(new CustomEvent('app-logo-updated', { detail: resolved && logoMedia ? logoMedia : null }));
          }
        }
      } catch (error) {
        console.warn('Logo konnte nicht geladen werden', error);
      }
    };

    fetchLogo();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <LogoWrapper>
      {logoSrc && <img src={logoSrc} width='250px' alt="Logo" />}
    </LogoWrapper>
  );
}

export default Logo;
